export default function TimelineSection() {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
            Act Fast: Texas Foreclosure Timeline
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Understanding the foreclosure timeline in Texas helps you know when to act. The sooner you contact us, the more options you have to avoid foreclosure and protect your financial future.
          </p>
        </div>

        <div className="relative mb-16">
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-teal-600 hidden lg:block"></div>
          
          <div className="space-y-12">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div className="lg:text-right">
                <div className="bg-white rounded-xl p-8 shadow-xl inline-block">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4">
                    <i className="ri-calendar-line text-3xl text-yellow-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Day 1-30: First Missed Payment</h3>
                  <p className="text-base text-gray-700 leading-relaxed mb-4">
                    You miss your first mortgage payment. Your lender will send a payment reminder. This is the best time to avoid foreclosure - contact us immediately for a cash offer before the situation escalates.
                  </p>
                  <div className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-bold">
                    <i className="ri-time-line mr-1"></i>
                    Best Time to Act
                  </div>
                </div>
              </div>
              <div className="hidden lg:block">
                <img 
                  src="https://public.readdy.ai/ai/img_res/22f7acaff8ddc879714a143a413d169f.jpg" 
                  alt="First missed mortgage payment" 
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div className="lg:order-2">
                <div className="bg-white rounded-xl p-8 shadow-xl inline-block">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
                    <i className="ri-mail-line text-3xl text-orange-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Day 30-90: Late Payment Notices</h3>
                  <p className="text-base text-gray-700 leading-relaxed mb-4">
                    After 30 days, late fees are added. At 60-90 days, you'll receive more urgent notices and collection calls. Your lender reports the late payments to credit bureaus. You still have time to avoid foreclosure by selling quickly.
                  </p>
                  <div className="inline-block bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full text-sm font-bold">
                    <i className="ri-alert-line mr-1"></i>
                    Time Running Out
                  </div>
                </div>
              </div>
              <div className="lg:order-1">
                <img 
                  src="https://readdy.ai/api/search-image?query=stack-of-late-payment-notices-and-collection-letters-on-desk-with-worried-homeowner-in-background-realistic-photography-showing-mounting-financial-pressure-natural-lighting-serious-tone-mortgage-default-warning&width=600&height=400&seq=avoid-foreclosure-timeline-012&orientation=landscape" 
                  alt="Late payment notices and collection calls" 
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div className="lg:text-right">
                <div className="bg-white rounded-xl p-8 shadow-xl inline-block">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                    <i className="ri-file-warning-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Day 120: Notice of Default (NOD)</h3>
                  <p className="text-base text-gray-700 leading-relaxed mb-4">
                    After 120 days of missed payments, your lender files a Notice of Default. This is a formal declaration that you're in default on your mortgage. Foreclosure proceedings begin. Contact us now to avoid foreclosure - we can still help!
                  </p>
                  <div className="inline-block bg-red-100 text-red-800 px-4 py-2 rounded-full text-sm font-bold">
                    <i className="ri-alarm-warning-line mr-1"></i>
                    Critical Stage
                  </div>
                </div>
              </div>
              <div className="hidden lg:block">
                <img 
                  src="https://readdy.ai/api/search-image?query=official-notice-of-default-document-with-red-stamp-on-wooden-desk-legal-paperwork-close-up-dramatic-lighting-showing-serious-foreclosure-proceedings-beginning-realistic-photography-professional-legal-setting&width=600&height=400&seq=avoid-foreclosure-timeline-013&orientation=landscape" 
                  alt="Notice of Default foreclosure document" 
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div className="lg:order-2">
                <div className="bg-white rounded-xl p-8 shadow-xl inline-block">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                    <i className="ri-auction-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Day 141: Notice of Trustee Sale</h3>
                  <p className="text-base text-gray-700 leading-relaxed mb-4">
                    Your lender posts a Notice of Trustee Sale at the courthouse and on your property. This notice sets the foreclosure auction date - exactly 21 days away. This is your last chance to avoid foreclosure. Call us immediately!
                  </p>
                  <div className="inline-block bg-red-100 text-red-800 px-4 py-2 rounded-full text-sm font-bold">
                    <i className="ri-error-warning-line mr-1"></i>
                    Final Warning
                  </div>
                </div>
              </div>
              <div className="lg:order-1">
                <img 
                  src="https://readdy.ai/api/search-image?query=foreclosure-auction-notice-posted-on-courthouse-bulletin-board-official-legal-posting-realistic-photography-showing-public-notice-of-trustee-sale-serious-tone-dramatic-lighting-texas-courthouse-setting&width=600&height=400&seq=avoid-foreclosure-timeline-014&orientation=landscape" 
                  alt="Notice of Trustee Sale at courthouse" 
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div className="lg:text-right">
                <div className="bg-red-600 rounded-xl p-8 shadow-xl inline-block text-white">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
                    <i className="ri-hammer-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-3">Day 162: Foreclosure Auction</h3>
                  <p className="text-base leading-relaxed mb-4">
                    Your home is sold at public auction on the courthouse steps on the first Tuesday of the month. The highest bidder takes ownership. You lose your home and receive nothing. After this, it's too late to avoid foreclosure through us.
                  </p>
                  <div className="inline-block bg-white/20 px-4 py-2 rounded-full text-sm font-bold">
                    <i className="ri-close-circle-line mr-1"></i>
                    Too Late
                  </div>
                </div>
              </div>
              <div className="hidden lg:block"></div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 md:p-12 text-white text-center">
          <h3 className="text-3xl md:text-4xl font-black mb-6">
            Don't Wait Until Day 162
          </h3>
          <p className="text-xl mb-8 max-w-3xl mx-auto leading-relaxed">
            We can close in as little as 7 days, but we need time to work with your lender and complete the paperwork. The earlier you contact us in the foreclosure timeline, the more options we have to help you avoid foreclosure.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <a 
              href="tel:+1-915-206-5814" 
              className="bg-white hover:bg-gray-100 text-gray-900 px-10 py-4 rounded-lg text-lg font-bold transition-all shadow-2xl cursor-pointer whitespace-nowrap w-full sm:w-auto text-center"
            >
              <i className="ri-phone-fill mr-2"></i>
              Call (915) 206-5814 Now
            </a>
            <a 
              href="#contact" 
              className="bg-teal-800 hover:bg-teal-900 text-white px-10 py-4 rounded-lg text-lg font-bold transition-all shadow-2xl cursor-pointer whitespace-nowrap w-full sm:w-auto text-center"
            >
              Get Your Cash Offer
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}